/**
 * «Индексировать» на «ЗП → Индексация»: ставка до и после, кому и текст → «Поднять ставку и отправить».
 * Пишет `sUser.zpHour` в CRM (как ручная правка карточки), затем сообщение с личного Telegram владельца.
 * Право `salary_index`; спека `docs/specs/2026-11-04-salary-index-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { money } from "@/lib/bonus";
import { applySalaryRaise, loadSalaryRaise, resendSalaryRaise, type SalaryRaise } from "@/lib/salary";

const date = (d: string | null) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : "—");

function Result({ r }: { r: SalaryRaise }) {
  const sent = r.status === "sent";
  return (
    <div className={sent ? "text-ok" : "text-warn"} role="status">
      Ставка в CRM: {money(r.old)} → {money(r.new)} ₽.{" "}
      {sent ? "Сообщение отправлено." : r.text ? `Сообщение не ушло: ${r.error ?? "—"}.` : "Без сообщения."}
    </div>
  );
}

export function SalaryRaiseDialog({ user, onClose: close_ }: { user: number; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["salary-raise", user], queryFn: () => loadSalaryRaise(user), staleTime: 0 });
  const [text, setText] = useState<string | null>(null);
  const [notify, setNotify] = useState(true);
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => close.current?.focus(), []);
  const p = q.data;
  const body = text ?? p?.text ?? "";
  const send = notify && !!p?.to;
  const apply = useMutation({
    mutationFn: () => applySalaryRaise(user, { old: p?.old ?? 0, new: p?.new ?? 0, text: send ? body.trim() : "", send }),
  });
  const resend = useMutation({ mutationFn: (id: number) => resendSalaryRaise(id) });
  // Отчёт обновляем при закрытии: строка уйдёт из «пора индексировать» вместе с итогом в окне.
  const onClose = () => {
    if (apply.isSuccess) void qc.invalidateQueries({ queryKey: ["salary-index"] });
    close_();
  };
  const done = resend.data?.raise ?? apply.data?.raise;
  const telegramBad = send && !p?.telegram_ok;
  const blocked = p?.blocked ?? (telegramBad ? p?.telegram_error ?? "Telegram недоступен" : null);
  const btn =
    "h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[12.5px] text-fg hover:bg-bg-hover disabled:opacity-40";

  return createPortal(
    <div
      className="fixed inset-0 z-[90] grid place-items-center bg-black/30 p-4"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && (e.stopPropagation(), onClose())}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Индексация ставки"
        className="flex max-h-[92vh] w-full max-w-xl flex-col gap-3 overflow-auto rounded-[var(--radius-ui)] border border-border bg-bg p-4 text-[12.5px] text-fg shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-annot="индексировать ставку"
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 text-[14px] font-medium">Индексация ставки{p ? ` · ${p.user.name}` : ""}</div>
          <button
            ref={close}
            type="button"
            className="grid size-6 place-items-center rounded text-fg-muted hover:bg-bg-hover hover:text-fg"
            aria-label="Закрыть"
            title="Закрыть (Esc)"
            onClick={onClose}
          >
            <X size={14} aria-hidden />
          </button>
        </div>

        {q.isPending && <div className="text-fg-muted">Пересчитываю стаж по CRM…</div>}
        {q.isError && <div className="text-err">Не удалось загрузить: {String(q.error.message)}</div>}

        {p && p.new !== null && (
          <>
            <div>
              <div className="text-[15px]">
                Ставка часа: {money(p.old)} → <b className="font-semibold tabular-nums">{money(p.new)} ₽</b>
              </div>
              <div className="mt-0.5 text-fg-muted">
                {p.role.name}, стаж {p.years ?? "—"} г.; положено с {date(p.since)}.
                {p.next_date && p.next_rate !== null && ` Следующая индексация — ${date(p.next_date)}, до ${money(p.next_rate)} ₽.`}
              </div>
              <div className="mt-0.5 text-fg-faint">
                Запишем в карточку сотрудника CRM, как ручную правку: в журнале изменений — от вашего имени.
              </div>
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={send} disabled={!p.to || !!done} onChange={(e) => setNotify(e.target.checked)} />
              <span>
                Сообщение сотруднику{" "}
                {p.to ? (
                  <span className="text-fg-muted">
                    · {p.to.login ? `@${p.to.login}` : `id ${p.to.tg_id}`} · с личного Telegram владельца
                  </span>
                ) : (
                  <span className="text-warn">· Telegram не найден — поднимем только ставку</span>
                )}
              </span>
            </label>
            {send && (
              <textarea
                value={body}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                disabled={!!done}
                aria-label="Текст сообщения"
                className="rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1.5 text-[12.5px] text-fg outline-none focus:border-accent"
              />
            )}
          </>
        )}

        {blocked && !done && (
          <div className="text-err" role="alert">
            {blocked}
          </div>
        )}
        {(apply.isError || resend.isError) && (
          <div className="text-err" role="alert">
            Не получилось: {String((apply.error ?? resend.error)?.message)}
          </div>
        )}
        {done && <Result r={done} />}

        <div className="flex flex-wrap items-center gap-2">
          {!done && (
            <button
              type="button"
              className={`${btn} border-accent`}
              disabled={!p || p.new === null || !!blocked || apply.isPending || (send && !body.trim())}
              onClick={() => apply.mutate()}
            >
              {apply.isPending ? "Записываю в CRM…" : send ? "Поднять ставку и отправить" : "Поднять ставку"}
            </button>
          )}
          {done?.status === "send_failed" && (
            <button type="button" className={btn} disabled={resend.isPending} onClick={() => resend.mutate(done.id)}>
              {resend.isPending ? "Отправляю…" : "Отправить ещё раз"}
            </button>
          )}
          <button type="button" className={btn} onClick={onClose}>
            {done ? "Готово" : "Отмена"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
